import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCrown } from 'react-icons/fa';

import LoadingOverlay from '@/components/LoadingOverlay';
import ResponsiveModalPage from '@/components/ResponsiveModalPage';
import { toast } from '@/components/toastStore';
import { PackageType, PaymentType } from '@/models';
import type {
  Balance,
  CustomerPackage,
  SelectedPackage,
  ValidatePackagesResponse,
} from '@/models';
import BookingCostSummary from '@/pages/components/BookingCostSummary';
import {
  CashWalletPackageBottomSheet,
  MinutesPackageBottomSheet,
  TimesPackageBottomSheet,
} from '@/pages/components/bottomsheets';
import {
  BookingService,
  ProfileService,
  useStoredPhoneNumber,
  useStoredProfile,
} from '@/services';
import { useBrandStore } from '@/store';
import { useBookingStore } from '@/store/bookingStore';

interface PackageSelectionModalPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const PackageSelectionModalPage: React.FC<PackageSelectionModalPageProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    name,
    telPrefix,
    tel,
    selectedPackages,
    balance: storeBalance,
  } = useBookingStore();
  const { getPaymentType } = useBrandStore();
  const storedProfile = useStoredProfile();
  const storedPhoneNumber = useStoredPhoneNumber();
  const [packages, setPackages] = useState<CustomerPackage[]>([]);
  const [activePackage, setActivePackage] = useState<CustomerPackage | null>(
    null,
  );
  const [localSelected, setLocalSelected] = useState<SelectedPackage[]>([]);
  const [previewSelectedPackages, setPreviewSelectedPackages] = useState<
    SelectedPackage[] | null
  >(null);
  const [previewBalance, setPreviewBalance] = useState<Balance | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [showCashWalletSheet, setShowCashWalletSheet] = useState(false);
  const [showTimesSheet, setShowTimesSheet] = useState(false);
  const [showMinutesSheet, setShowMinutesSheet] = useState(false);

  const phoneNumber = `${telPrefix || '+66'}${tel || ''}`;
  const isDeposit =
    getPaymentType() === PaymentType.Deposit &&
    storeBalance.depositTotal < storeBalance.total;

  const loadPackages = useCallback(async (): Promise<CustomerPackage[]> => {
    if (ProfileService.isLoggedIn()) {
      const response = await ProfileService.getMyCustomerPackages({
        usable: true,
      });
      return response.items;
    }

    return await BookingService.getCustomerPackages(phoneNumber);
  }, [phoneNumber]);

  useEffect(() => {
    if (!isOpen) return; // only load when opened
    setLocalSelected(selectedPackages || []);
    setPreviewSelectedPackages(null);
    setPreviewBalance(null);
    (async () => {
      try {
        setLoading(true);
        const customerPackages = await loadPackages();
        setPackages(customerPackages);
      } catch (e) {
        console.error('Failed to load packages', e);
        setError('Failed to load packages.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, loadPackages, selectedPackages]);

  const isChecked = (id: number) => localSelected.some((p) => p.id === id);

  const formatPackageAmount = (value: number, type?: PackageType): string => {
    const base = value.toLocaleString();

    switch (type) {
      case PackageType.CashWallet:
        return `${base} ${t('package.unit.baht')}`;
      case PackageType.Times:
        return `${base} ${t('package.unit.times')}`;
      case PackageType.Minutes:
        return `${base} ${t('package.unit.minutes')}`;
      case PackageType.Percent:
        return `${base}%`;
      default:
        return base;
    }
  };

  const showAdjustmentToasts = (
    attemptedSelection: SelectedPackage[],
    validationResult: ValidatePackagesResponse,
  ) => {
    const attemptedPackagesById = new Map(
      attemptedSelection.map((pkg) => [pkg.id, pkg]),
    );

    validationResult.selectedPackages
      .filter((pkg) => {
        if (pkg.valid !== false) return false;

        const attemptedPackage = attemptedPackagesById.get(pkg.id);
        const previousValue = pkg.previousValue ?? attemptedPackage?.value;

        return previousValue !== undefined && previousValue !== pkg.value;
      })
      .forEach((pkg) => {
        toast.warning(
          t('package.toast.adjusted', {
            value: formatPackageAmount(pkg.value, pkg.type),
          }),
        );
      });
  };

  const buildPreviewBalance = (
    validationResult: ValidatePackagesResponse,
  ): Balance => ({
    promotions: validationResult.promotions ?? [],
    promotionDiscount: validationResult.promotionDiscount ?? 0,
    packageDiscount: validationResult.packageDiscount ?? 0,
    subtotal: validationResult.subtotal ?? 0,
    total: validationResult.total ?? 0,
    totalDiscount: validationResult.totalDiscount ?? 0,
    depositTotal: validationResult.depositTotal ?? 0,
    depositValue: validationResult.depositValue ?? 0,
    remainingTotal: validationResult.remainingTotal ?? 0,
  });

  const applyValidationResult = (
    attemptedSelection: SelectedPackage[],
    validationResult: ValidatePackagesResponse,
  ) => {
    showAdjustmentToasts(attemptedSelection, validationResult);

    const correctedPackagesById = new Map(
      validationResult.selectedPackages
        .filter((pkg) => pkg.valid === false)
        .map((pkg) => [pkg.id, pkg]),
    );

    const nextSelected =
      correctedPackagesById.size > 0
        ? attemptedSelection.map(
            (selectedPkg) =>
              correctedPackagesById.get(selectedPkg.id) ??
              validationResult.selectedPackages.find(
                (pkg) => pkg.id === selectedPkg.id,
              ) ??
              selectedPkg,
          )
        : validationResult.selectedPackages;

    const filteredSelected = nextSelected.filter((pkg) => pkg.value > 0);

    setLocalSelected(filteredSelected);
    setPreviewSelectedPackages(filteredSelected);
    setPreviewBalance(buildPreviewBalance(validationResult));

    return correctedPackagesById.size > 0;
  };

  const openConfig = (pkg: CustomerPackage) => {
    setActivePackage(pkg);
    switch (pkg.type) {
      case PackageType.CashWallet:
        setShowCashWalletSheet(true);
        break;
      case PackageType.Times:
        setShowTimesSheet(true);
        break;
      case PackageType.Minutes:
        setShowMinutesSheet(true);
        break;
      case PackageType.Percent: {
        // toggle instantly using remaining value
        handleValidateAdd(
          { id: pkg.id, value: pkg.remainingValue },
          isChecked(pkg.id),
        );
        break;
      }
      default:
        break;
    }
  };

  const handleCheckbox = async (
    e: React.MouseEvent,
    pkg: CustomerPackage,
    checked: boolean,
  ) => {
    e.stopPropagation();
    if (checked) {
      // Remove package - validate the removal
      await handleValidateAdd({ id: pkg.id, value: 0 }, true);
    } else {
      openConfig(pkg);
    }
  };

  const handleValidateAdd = async (
    incoming: SelectedPackage,
    removeIfExists = false,
  ) => {
    try {
      setValidating(true);
      setValidationError(''); // Clear previous validation errors

      let merged: SelectedPackage[];
      if (removeIfExists) {
        merged = localSelected.filter((p) => p.id !== incoming.id);
      } else {
        const without = localSelected.filter((p) => p.id !== incoming.id);
        const selectedPkgMeta = packages.find((p) => p.id === incoming.id);
        merged = [
          ...without,
          {
            ...incoming,
            name: selectedPkgMeta?.name,
            type: selectedPkgMeta?.type,
            remainingValue: selectedPkgMeta?.remainingValue,
            totalValue: selectedPkgMeta?.totalValue,
          },
        ];
      }

      // Call validation API
      const validationResult = await BookingService.validatePackages(merged);

      const hasCorrections = applyValidationResult(merged, validationResult);

      if (!validationResult.valid && !hasCorrections) {
        setValidationError(t('package.errors.invalidSelection'));
      }
    } catch (error) {
      console.error('Failed to validate packages:', error);
      setValidationError(t('package.errors.validateFailed'));
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmedPackage = (selected: SelectedPackage) => {
    handleValidateAdd(selected, false);
  };

  const handleUsePackages = async () => {
    try {
      setValidating(true);
      setValidationError('');

      const finalValidation =
        await BookingService.validatePackages(localSelected);

      if (!finalValidation.valid) {
        const hasCorrections = applyValidationResult(
          localSelected,
          finalValidation,
        );

        if (!hasCorrections) {
          setValidationError(t('package.errors.invalidSelection'));
        }

        return;
      }

      BookingService.persistValidatedPackages(finalValidation);

      setPreviewSelectedPackages(null);
      setPreviewBalance(null);
      onClose();
    } catch (error) {
      console.error('Failed to apply packages:', error);
      setValidationError(t('package.errors.applyFailed'));
    } finally {
      setValidating(false);
    }
  };

  const { t } = useTranslation();
  const isLoggedIn = ProfileService.isLoggedIn();

  const contactName = useMemo(() => {
    if (isLoggedIn) {
      return storedProfile?.name || name || 'Customer';
    }

    return name || 'Customer';
  }, [isLoggedIn, name, storedProfile?.name]);

  const phoneDisplay = useMemo(() => {
    if (isLoggedIn) {
      if (storedProfile?.telPrefix && storedProfile.tel) {
        return `${storedProfile.telPrefix}${storedProfile.tel}`;
      }

      return storedPhoneNumber || `${telPrefix || ''}${tel || ''}`;
    }

    return `${telPrefix || ''}${tel || ''}`;
  }, [isLoggedIn, storedPhoneNumber, storedProfile, tel, telPrefix]);

  const effectiveSelectedPackages = previewSelectedPackages ?? localSelected;
  const shouldDisableUnselectedPackages =
    effectiveSelectedPackages.length > 0 &&
    (previewBalance?.total ?? storeBalance.total) === 0;

  const content = (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {loading && (
        <>
          <div className="mt-2 flex h-8 items-center justify-center gap-2 px-4">
            <div className="h-5 w-5 animate-pulse rounded bg-gray-200"></div>
            <div className="h-5 w-32 animate-pulse rounded bg-gray-200"></div>
          </div>
          <div className="mt-4 px-4">
            <div className="h-6 w-24 animate-pulse rounded bg-gray-200"></div>
          </div>
          <div className="space-y-4 p-4">
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="border-border-inactive flex items-start gap-2 rounded-lg border bg-white px-2 py-2"
                >
                  <div className="pt-[2px]">
                    <div className="h-5 w-5 animate-pulse rounded bg-gray-200"></div>
                  </div>
                  <div className="flex flex-1 flex-col justify-start overflow-hidden">
                    <div className="mb-1 h-6 w-3/4 animate-pulse rounded bg-gray-200"></div>
                    <div className="h-6 w-1/2 animate-pulse rounded bg-gray-200"></div>
                  </div>
                  <div className="flex min-w-[70px] flex-col items-end justify-center pl-2 text-right">
                    <div className="h-6 w-16 animate-pulse rounded bg-gray-200"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!loading && error && (
        <div className="p-6 text-center">
          <div className="mb-4 text-5xl text-red-500">⚠️</div>
          <p className="mb-4 text-gray-600">{error}</p>
          <button
            type="button"
            className="bg-primary-text rounded px-4 py-2 text-sm text-white"
            onClick={() => {
              setError('');
              setLoading(true);
              loadPackages()
                .then((p) => setPackages(p))
                .catch(() => setError(t('package.failedToLoad')))
                .finally(() => setLoading(false));
            }}
          >
            {t('common.tryAgain')}
          </button>
        </div>
      )}
      {!loading && !error && (
        <>
          <div className="mt-2 flex h-8 items-center justify-center gap-2 px-4">
            <FaCrown className="text-text-muted" />
            <div className="text-primary-text text-lg leading-7 font-normal">
              {contactName} ({phoneDisplay})
            </div>
          </div>
          <div className="mt-4 px-4">
            <h2 className="text-primary-text leading-6 font-medium">
              {t('package.details')}
            </h2>
            {validationError && (
              <div className="mt-2 flex w-full items-start gap-2">
                <div className="text-error text-left text-sm leading-5 font-normal">
                  {validationError}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4 p-4">
            <div className="space-y-4">
              {packages.map((pkg) => {
                const secondLine = (() => {
                  switch (pkg.type) {
                    case PackageType.CashWallet:
                      return `(${pkg.remainingValue.toLocaleString()}/${pkg.totalValue.toLocaleString()} ${t('package.unit.baht')})`;
                    case PackageType.Times:
                      return `(${pkg.remainingValue}/${pkg.totalValue} ${t('package.unit.times')})`;
                    case PackageType.Minutes:
                      return `(${pkg.remainingValue}/${pkg.totalValue} ${t('package.unit.minutes')})`;
                    case PackageType.Percent:
                      return '';
                    default:
                      return '';
                  }
                })();
                const checked = isChecked(pkg.id);
                const isDisabled =
                  (isDeposit &&
                    (pkg.type === PackageType.Times ||
                      pkg.type === PackageType.Minutes)) ||
                  (shouldDisableUnselectedPackages && !checked);
                return (
                  <div
                    key={pkg.id}
                    className={`flex items-start gap-2 rounded-lg border px-2 py-2 ${checked ? 'border-primary bg-bg-selected' : isDisabled ? 'border-border-disabled cursor-not-allowed bg-white' : 'border-border-inactive cursor-pointer bg-white'}`}
                    onClick={() => {
                      if (isDisabled) return;
                      openConfig(pkg);
                    }}
                    aria-disabled={isDisabled}
                  >
                    <div className="pt-[2px]">
                      <button
                        type="button"
                        onClick={(e) => handleCheckbox(e, pkg, checked)}
                        className={`flex h-5 w-5 items-center justify-center rounded border ${checked ? 'border-primary bg-primary focus:ring-primary focus:ring-2 focus:ring-offset-1 focus:outline-none' : isDisabled ? 'border-border-disabled bg-white' : 'border-border-inactive focus:ring-primary bg-white focus:ring-2 focus:ring-offset-1 focus:outline-none'}`}
                        aria-pressed={checked}
                        aria-label={
                          checked ? 'Deselect package' : 'Select package'
                        }
                        disabled={isDisabled}
                      >
                        {checked && (
                          <svg
                            className="h-3 w-3 text-white"
                            viewBox="0 0 12 10"
                            fill="none"
                          >
                            <path
                              d="M1 5L4.5 8.5L11 1.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    <div className="flex flex-1 flex-col justify-start overflow-hidden">
                      <p
                        className={`truncate leading-6 font-medium ${isDisabled ? 'text-text-disabled' : 'text-primary-text'}`}
                      >
                        {pkg.name}
                      </p>
                      {secondLine && (
                        <p
                          className={`truncate leading-6 ${isDisabled ? 'text-text-disabled' : 'text-secondary-text'}`}
                        >
                          {secondLine}
                        </p>
                      )}
                    </div>
                    <div className="flex min-w-[70px] flex-col items-center justify-center pl-2 text-right">
                      {checked && (
                        <span
                          className={`leading-6 font-medium ${isDisabled ? 'text-text-disabled' : 'text-secondary'}`}
                        >
                          {(() => {
                            const sel = localSelected.find(
                              (s) => s.id === pkg.id,
                            );
                            if (!sel) return '';
                            switch (pkg.type) {
                              case PackageType.CashWallet:
                                return `${sel.value.toLocaleString()} ${t('package.unit.baht')}`;
                              case PackageType.Times:
                                return `${sel.value} ${t('package.unit.times')}`;
                              case PackageType.Minutes:
                                return `${sel.value} ${t('package.unit.minutes')}`;
                              case PackageType.Percent:
                                return `${sel.value}%`;
                              default:
                                return sel.value;
                            }
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <ResponsiveModalPage
      isOpen={isOpen}
      onClose={onClose}
      title="Select Packages"
      desktopWidthClass="md:w-[640px]"
      desktopHeightClass="md:h-[80vh]"
      stickyFooter={
        <BookingCostSummary
          totalLabel="Total Remaining"
          confirmLabel="Use Package"
          overrideSelectedPackages={previewSelectedPackages ?? undefined}
          overrideBalance={previewBalance ?? undefined}
          onConfirm={handleUsePackages}
        />
      }
    >
      {content}
      {/* Config Sheets */}
      <CashWalletPackageBottomSheet
        isOpen={showCashWalletSheet}
        onClose={() => setShowCashWalletSheet(false)}
        customerPackage={
          activePackage && activePackage.type === PackageType.CashWallet
            ? activePackage
            : null
        }
        initialValue={
          activePackage
            ? localSelected.find((p) => p.id === activePackage.id)?.value
            : undefined
        }
        onConfirm={handleConfirmedPackage}
      />
      <TimesPackageBottomSheet
        isOpen={showTimesSheet}
        onClose={() => setShowTimesSheet(false)}
        customerPackage={
          activePackage && activePackage.type === PackageType.Times
            ? activePackage
            : null
        }
        initialValue={
          activePackage
            ? localSelected.find((p) => p.id === activePackage.id)?.value
            : undefined
        }
        onConfirm={handleConfirmedPackage}
      />
      <MinutesPackageBottomSheet
        isOpen={showMinutesSheet}
        onClose={() => setShowMinutesSheet(false)}
        customerPackage={
          activePackage && activePackage.type === PackageType.Minutes
            ? activePackage
            : null
        }
        initialValue={
          activePackage
            ? localSelected.find((p) => p.id === activePackage.id)?.value
            : undefined
        }
        onConfirm={handleConfirmedPackage}
      />

      <LoadingOverlay isVisible={validating} />
    </ResponsiveModalPage>
  );
};

export default PackageSelectionModalPage;
